import { useEffect, useState } from "react";
import { Loader2, Building2 } from "lucide-react";

import { useOrganization } from "@/context/OrganizationContext";
import { OrganizationService } from "@/services/organization.service";
import { ROLE_LABELS, type OrganizationRole } from "@/constants/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertMessage } from "@/components/ui/alert-message";
import { MembersTable } from "@/components/organization/members-table";
import { InvitationsTable } from "@/components/organization/invitations-table";
import { AuditLogTable } from "@/components/organization/audit-log-table";

export default function OrganizationPage() {
  const { organization, membership, can, isLoading, refresh, graceMode } = useOrganization();

  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (organization) setName(organization.name);
  }, [organization]);

  const isOwner = membership?.role === "owner";
  const canViewTeam = can("team");
  const canViewAudit = membership?.role === "owner" || membership?.role === "admin";

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === organization?.name) return;
    setIsSaving(true);
    setError("");
    setSuccess("");
    try {
      await OrganizationService.rename(name.trim());
      refresh();
      setSuccess("Organization name updated.");
    } catch (err: any) {
      setError(err?.message || "Failed to rename the organization.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (graceMode || !organization) {
    return (
      <Card className="mx-auto mt-12 max-w-lg">
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <Building2 className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Organization is being prepared</h2>
          <p className="text-sm text-muted-foreground">
            Your account has not been migrated to the new organizations model yet. Team
            management will appear here shortly.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Organization</h1>
        <p className="text-muted-foreground">
          Manage your organization, team members and permissions.
        </p>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          {canViewTeam && <TabsTrigger value="members">Members</TabsTrigger>}
          {canViewTeam && <TabsTrigger value="invitations">Invitations</TabsTrigger>}
          {canViewAudit && <TabsTrigger value="audit">Audit Log</TabsTrigger>}
        </TabsList>

        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General</CardTitle>
              <CardDescription>Your organization's profile.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <AlertMessage message={error} type="error" />
              <AlertMessage message={success} type="success" />

              <form onSubmit={handleRename} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization name</Label>
                  <Input
                    id="org-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!isOwner || isSaving}
                    maxLength={100}
                  />
                  {!isOwner && (
                    <p className="text-xs text-muted-foreground">
                      Only the organization owner can change the name.
                    </p>
                  )}
                </div>
                {isOwner && (
                  <Button
                    type="submit"
                    disabled={isSaving || !name.trim() || name.trim() === organization.name}
                  >
                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save changes
                  </Button>
                )}
              </form>

              <div className="grid gap-4 sm:grid-cols-3 max-w-2xl">
                <div>
                  <p className="text-xs text-muted-foreground">Your role</p>
                  <Badge className="mt-1">
                    {ROLE_LABELS[(membership?.role ?? "member") as OrganizationRole] ??
                      membership?.role}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p className="mt-1 text-sm">
                    {new Date(organization.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Organization ID</p>
                  <p className="mt-1 truncate font-mono text-xs">{organization.id}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {canViewTeam && (
          <TabsContent value="members">
            <Card>
              <CardHeader className="pb-0">
                <CardTitle>Members</CardTitle>
                <CardDescription>Manage who has access and what they can do.</CardDescription>
              </CardHeader>
              <CardContent className="p-0 pb-4">
                <MembersTable />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canViewTeam && (
          <TabsContent value="invitations">
            <Card>
              <CardHeader className="pb-0">
                <CardTitle>Invitations</CardTitle>
                <CardDescription>Pending invitations to your organization.</CardDescription>
              </CardHeader>
              <CardContent className="p-0 pb-4">
                <InvitationsTable />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canViewAudit && (
          <TabsContent value="audit">
            <Card>
              <CardHeader className="pb-0">
                <CardTitle>Audit Log</CardTitle>
                <CardDescription>
                  Every change and security event in your organization, newest first.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <AuditLogTable />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
